import React, { ReactNode, useEffect } from 'react';
import { Navigate, useLocation, useNavigate } from 'react-router-dom';
import AuthRoutes from '../../AuthRoutes';
import HAppRoutes from '../../../../app/features/routes/helpers/HAppRoutes';

import { useAuthContext } from '../../contexts/AuthContext';
import { useOktaAuth } from '@okta/okta-react';
import { useMatomo } from '@jonkoops/matomo-tracker-react';
import { globalConfig } from '../../../../app/config/config';
import { useOidc, useOidcIdToken } from '@axa-fr/react-oidc';

type AuthGateProps = {
	authRequired: boolean;
	children: ReactNode;
	currentUri: string;
};
const AuthGate = (props: AuthGateProps) => {
	const { authRequired, children, currentUri } = props;
	const auth = useAuthContext();

	const navigate = useNavigate();
	const { oktaAuth } = useOktaAuth();
	const oidc = useOidc();

	// Set redirect uri if user is not authenticated
	useEffect(() => {
		if (auth && !auth.isAuthenticated && authRequired) {
			auth.setRedirectUri(currentUri);
		}
	}, [authRequired, auth, currentUri]);

	useEffect(() => {
		if (authRequired && !auth.isAuthenticated) {
			navigate(HAppRoutes.getPath(AuthRoutes.logout.tag));
		}
	}, [oktaAuth, authRequired, auth, navigate, oidc]);

	const { trackPageView, pushInstruction } = useMatomo();
	const currentPage = useLocation();
	const { idTokenPayload } = useOidcIdToken();
	useEffect(() => {
		if (
			!['/login', '/login/callback/employee', '/login/callback/employee', '/callback', '/logout'].includes(
				currentPage.pathname,
			)
		) {
			pushInstruction('setUserId', idTokenPayload?.email ?? auth?.authUserProfile?.email);
			pushInstruction('setCustomDimension', globalConfig.get().APP_MATOMO_AUTH_CONTEXT_CD_ID, auth?.authContext);
			pushInstruction(
				'setCustomDimension',
				globalConfig.get().APP_MATOMO_CURRENT_COMPANY_CD_ID,
				auth?.currentCompany?.name || 'not_defined',
			);
			trackPageView();
		}
	}, [
		auth?.authContext,
		auth?.authUserProfile?.email,
		auth?.currentCompany?.name,
		currentPage.pathname,
		idTokenPayload?.email,
		pushInstruction,
		trackPageView,
	]);

	return (
		<>
			<>
				{authRequired === false || (authRequired && auth?.isAuthenticated) ? (
					children
				) : (
					<Navigate to={HAppRoutes.getPath(AuthRoutes.logout.tag)} replace />
				)}
			</>
		</>
	);
};

export default AuthGate;
